/**
 * The `/v1` client.
 *
 * This console is a consumer of the PUBLIC API (decision 0005) — the same contract a third-party
 * developer gets. There is no privileged internal surface, which is what keeps the public API
 * honest: if the console can do something a customer's application cannot, we find out while
 * building it rather than after shipping it.
 *
 * Every refusal from `/v1` is the same RFC-7807-shaped document with a required, stable `code`
 * (lib/http/api-problem.ts on the server). So this client never throws a bare string — it throws an
 * `ApiProblem` carrying the code, which is what the UI branches on. `detail` is written for a human
 * reading a log and is never parsed.
 */

export interface ApiProblemBody {
  type: string;
  title: string;
  status: number;
  detail: string;
  code: string;
  [key: string]: unknown;
}

export class ApiProblem extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly body: ApiProblemBody | null;

  constructor(status: number, code: string, title: string, detail: string, body: ApiProblemBody | null) {
    super(detail);
    this.name = "ApiProblem";
    this.status = status;
    this.code = code;
    this.title = title;
    this.body = body;
  }

  /** The caller has no usable credential — a distinct state, not a generic failure. */
  get isUnauthenticated(): boolean {
    return this.status === 401 || this.code === "UNAUTHORIZED";
  }

  /** The Email product is switched off for this deployment. Also a state, not an error. */
  get isProductDisabled(): boolean {
    return this.code === "EMAIL_PRODUCT_DISABLED";
  }

  /** The workspace has no Email subscription, or the plan lacks the feature. */
  get needsUpgrade(): boolean {
    return this.code === "EMAIL_CAPABILITY_REQUIRED";
  }
}

/**
 * Where the bearer token comes from.
 *
 * ═══ This is the customer's PulseAssist SESSION, not an API key ══════════════
 *
 * `/v1` accepts a first-party session token as well as an API key
 * (lib/middleware/tenant-api-key.ts). A customer should not have to mint an API key to look at their
 * own logs — keys are for external integrations.
 *
 * Sent as `Authorization: Bearer`, deliberately NOT as a cookie. A cookie would make every
 * state-changing `/v1` endpoint CSRF-able from any site the customer happens to visit, because a
 * browser attaches cookies to cross-site requests on its own. A header cannot be set cross-site
 * without a CORS preflight the server refuses. It is also what lets this console live on its own
 * origin without SameSite gymnastics.
 *
 * The session grants exactly the scopes a dashboard-minted key carries and reaches exactly the
 * workspaces its user reaches — there is no privileged console path.
 *
 * Obtaining and renewing it lives in `lib/session.ts`, which is where the 15-minute access-token
 * lifetime and the single-flight refresh are dealt with.
 */
import { accessToken, isSignedIn, refreshSession, ensureFreshToken } from "./session";
/** True when there is a credential to try. Not a claim that it is still valid. */
export function hasToken(): boolean {
  return isSignedIn();
}

const BASE = import.meta.env.VITE_API_ORIGIN ?? "";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  /*
   * Refresh BEFORE the request when the token is about to lapse, rather than waiting for the 401.
   * Access tokens live 15 minutes, so on a screen somebody leaves open this is the common case, and the
   * request simply carries a fresh token instead of failing and being retried.
   */
  await ensureFreshToken();

  const send = (token: string | null) =>
    fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });

  let res = await send(accessToken());

  /*
   * One retry, and only one.
   *
   * A 401 can still happen with a token that looked fresh — a wrong client clock, a session revoked on
   * another device, an `expiresIn` the server did not send. `refreshSession` is single-flight, so four
   * parallel requests all hitting 401 share ONE exchange; without that they would each rotate the
   * refresh token and three would present a value the server had just consumed, tripping its replay
   * limiter and killing the session at the exact moment it was meant to be renewed.
   *
   * Retrying more than once would loop against a genuinely dead session. `refreshSession` returns null
   * when the server has rejected the token, and that answer is final.
   */
  if (res.status === 401 && isSignedIn()) {
    const refreshed = await refreshSession();
    if (refreshed) res = await send(refreshed);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      /* fall through — handled below */
    }
  }

  if (!res.ok) {
    const body = (parsed && typeof parsed === "object" ? parsed : null) as ApiProblemBody | null;
    /*
     * A non-JSON error body means something in front of the API answered — a proxy, a CDN, an
     * origin that is not the API at all. Reported as such rather than as an API problem, because
     * "the API refused you" and "you never reached the API" need different responses from the user.
     */
    if (!body?.code) {
      throw new ApiProblem(
        res.status,
        "UNEXPECTED_RESPONSE",
        "Unexpected response",
        `The API returned ${res.status} without a problem document. The request may not have reached PulseAssist.`,
        null,
      );
    }
    throw new ApiProblem(body.status ?? res.status, body.code, body.title ?? "Request failed", body.detail ?? "", body);
  }

  return parsed as T;
}

/**
 * The same session-authenticated request, but for the `/api/developer/*` and `/api/api-keys` surface,
 * which predates `/v1` and answers failures as `{ error: "..." }` rather than an RFC-7807 document.
 *
 * ═══ Why these endpoints, and not /v1 ════════════════════════════════════════
 *
 * API keys and webhooks are the credentials FOR `/v1`. Minting or revoking one has to be authorised
 * by the first-party SESSION and the workspace owner — never by an API key, because a key that can
 * mint keys survives its own revocation, and a key that can delete webhooks could silence the alerts
 * that would reveal it had leaked. So these deliberately use the session routes (`requireAuth +
 * requireOwner`), which is the one place the console reaches outside `/v1` on purpose.
 *
 * The only real difference from `request()` is the error shape: this maps `{ error }` into an
 * `ApiProblem` so a screen renders the server's actual wording instead of the generic
 * "no problem document" fallback `request()` would produce for these older responses.
 */
async function developerRequest<T>(path: string, init?: RequestInit): Promise<T> {
  await ensureFreshToken();
  const send = (token: string | null) =>
    fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });

  let res = await send(accessToken());
  if (res.status === 401 && isSignedIn()) {
    const refreshed = await refreshSession();
    if (refreshed) res = await send(refreshed);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let parsed: unknown = null;
  if (text) { try { parsed = JSON.parse(text); } catch { /* handled below */ } }

  if (!res.ok) {
    const body = (parsed && typeof parsed === "object" ? parsed : null) as
      | { error?: string; code?: string; detail?: string }
      | null;
    // Map the legacy `{ error }` shape onto ApiProblem so callers have one error type to catch and
    // the server's own message survives. 401 keeps the UNAUTHORIZED code so `isUnauthenticated` works.
    const code = body?.code ?? (res.status === 401 ? "UNAUTHORIZED" : "REQUEST_FAILED");
    const message = body?.error ?? body?.detail ?? `The request failed (${res.status}).`;
    throw new ApiProblem(res.status, code, "Request failed", message, null);
  }
  return parsed as T;
}

// ─── Sends (Logs) ────────────────────────────────────────────────────────────

export interface ApiSend {
  id: string;
  status: string;
  to: string[];
  from: string;
  subject: string;
  provider: string;
  providerMessageId: string | null;
  idempotencyKey: string | null;
  errorDetail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiSendEvent {
  type: string;
  occurredAt: string;
  data: Record<string, unknown>;
}

/**
 * `GET /v1/email/analytics/overview`.
 *
 * Every rate is `number | null`, and the null is load-bearing rather than defensive: a rate over
 * zero accepted messages is undefined, not zero. Rendering `0%` there would state something false
 * about the customer's sending — the platform's rule, and the UI renders an em dash instead.
 */
export interface ApiOverview {
  windowDays: number;
  from: string;
  to: string;
  totals: {
    total: number;
    queued: number;
    sending: number;
    sent: number;
    delivered: number;
    bounced: number;
    complained: number;
    failed: number;
    /** sent + delivered + bounced + complained — the denominator every rate uses. */
    accepted: number;
  };
  rates: {
    deliveryRate: number | null;
    bounceRate: number | null;
    complaintRate: number | null;
  };
  daily: Array<{
    day: string;
    total: number;
    delivered: number;
    bounced: number;
    complained: number;
    failed: number;
  }>;
}

export interface ListSendsParams {
  limit?: number;
  /** Opaque cursor from the previous page's `nextCursor`. */
  cursor?: string | null;
  status?: string[];
  search?: string;
  since?: string;
}

/**
 * `GET /v1/domains`, and the shape the create/verify calls return too.
 *
 * This is the SERIALIZED view from lib/domains-api-view.ts, not a `tenant_domains` row — so there is
 * deliberately no `userId` or `workspaceId` here to bind to. Verification is grouped rather than
 * flattened because the four booleans are only meaningful together: a domain with DKIM verified and
 * SPF missing is not "half verified", it is not sending.
 */
export interface ApiDnsRecord {
  type: string;
  host: string;
  value: string;
  priority?: number;
  /** The provider's own view. `undefined` means NOT REPORTED, which is not the same as failing. */
  verified?: boolean;
}

/**
 * One DNS record's verification finding, exactly as the server's engine observed it.
 *
 * This is strictly better information than `ApiDnsRecord.verified`, which is the PROVIDER's
 * opinion: a check is our own live public-DNS lookup, and it carries what DNS actually
 * returned plus the specific next action for that record.
 */
export interface ApiDomainCheck {
  id: string;
  /** ownership | dkim | spf | dmarc | mx_bounce | mx_receiving */
  kind: string;
  label: string;
  type: string;
  host: string;
  expected: string;
  priority?: number;
  /** What DNS actually returned. Empty when nothing is published at this name. */
  found: string[];
  /**
   * pass | missing | mismatch | error | skipped.
   *
   * `missing` and `mismatch` are different on purpose — "wait for propagation" and "you
   * published the wrong value" are different instructions. `error` means OUR LOOKUP failed
   * and is not a statement about the customer's DNS at all.
   */
  status: string;
  detail: string;
  /** The next action for this one record. Null when it passes. */
  action: string | null;
  /** Does this record gate sending? DMARC and the MX records do not. */
  required: boolean;
  /** True for records that must be plain DNS, never proxied through a CDN. */
  proxySafe: boolean;
}

/**
 * The domain's status, DERIVED ON THE SERVER.
 *
 * This page used to compute its own headline from the raw booleans, and the admin console
 * computed a different one from the same table. Two derivations of "is this domain working"
 * disagree eventually, and the disagreement lands on the screen someone is staring at while
 * their mail is not sending. The server owns the vocabulary now; this file only renders it.
 */
// ─── The Email configuration chain (GET /api/email/configuration) ────────────

/**
 * `ok | warning | attention | pending | blocked | not_started | not_applicable | unknown`
 *
 * Left as a string rather than a union for the same reason `ApiDomainStatus.status` is: the server
 * owns this vocabulary, and a client union means a new state renders as a crash instead of an unknown
 * badge. Unrecognised values fall through to a neutral token.
 */
export type ApiSegmentState = string;

export interface ApiConfigurationSegment {
  id: "domain" | "dns" | "sending" | "receiving" | "mailboxes" | "deliverability";
  label: string;
  position: number;
  state: ApiSegmentState;
  summary: string;
  action: string | null;
  /**
   * The upstream link that must be dealt with FIRST, or null when this link is the place to act.
   *
   * This is the field that makes the page a chain rather than six cards: when it is set, the action
   * shown belongs to the blocker, and offering a button here would offer one that cannot work yet.
   */
  blockedBy: ApiConfigurationSegment["id"] | null;
  href: string;
  facts: Array<{ label: string; value: string }>;
}

export interface ApiChecklistStep {
  id: string;
  label: string;
  detail: string;
  done: boolean;
  blockedBy: ApiConfigurationSegment["id"] | null;
  segment: ApiConfigurationSegment["id"] | null;
  href: string;
}

export interface ApiPlanAllowance {
  key: string;
  label: string;
  /** `null` = not metered or unreadable. Never render as 0. */
  used: number | null;
  /** `null` = no fixed ceiling. The server never sends -1. */
  limit: number | null;
  remaining: number | null;
}

export interface ApiEmailPlanView {
  planKey: string | null;
  entitled: boolean;
  reason: string;
  enforcement: "off" | "log_only" | "enforce";
  allowances: ApiPlanAllowance[];
  /** Carries no price on purpose: `/plans` is the single pricing source. */
  upgrade: { available: boolean; detail: string; href: string };
}

export interface ApiIntegrationSummary {
  active: number | null;
  limit: number | null;
  state: ApiSegmentState;
  detail: string;
  href: string;
}

export interface ApiDeliverabilitySummary {
  windowDays: number | null;
  accepted: number | null;
  bounced: number | null;
  complained: number | null;
  /** Fractions, not percentages: `0.0617` is 6.17%. */
  bounceRate: number | null;
  complaintRate: number | null;
  deliveryRate: number | null;
  suppressed: number | null;
  state: ApiSegmentState;
  detail: string;
}

export interface ApiEmailConfiguration {
  chain: ApiConfigurationSegment[];
  checklist: {
    steps: ApiChecklistStep[];
    /** Steps that do not apply. Not for display — see the page's note on why they are sent at all. */
    hidden: Array<{ id: string; reason: string }>;
    doneCount: number;
    totalCount: number;
    complete: boolean;
  };
  plan: ApiEmailPlanView | null;
  apiKeys: ApiIntegrationSummary;
  webhooks: ApiIntegrationSummary;
  deliverability: ApiDeliverabilitySummary;
  /** Sources the server could not read, by name. */
  degraded: string[];
}

export interface ApiDomainStatus {
  /**
   * not_connected | setup_required | pending | action_required | verifying | connected |
   * connected_with_warning | failed | reverification_required | paused
   */
  status: string;
  /** Display wording, e.g. "Action required". Never re-derived here. */
  label: string;
  summary: string;
  /** The next action. Null ONLY for `connected`. */
  action: string | null;
  /** ok | info | warning | error | neutral — mapped to this app's own tokens, not a colour. */
  severity: string;
  /** False for `pending` and `verifying`: the next move is the server's, not the customer's. */
  requiresCustomerAction: boolean;
}

export interface ApiDomain {
  id: number;
  domain: string;
  isPrimary: boolean;
  /** pending_verification | verified | active | failed | paused */
  state: string;
  sendingEnabled: boolean;
  receivingEnabled: boolean;
  provider: { name: string; status: string };
  /** The headline. Server-owned; see ApiDomainStatus. */
  status: ApiDomainStatus;
  sending: { enabled: boolean; ready: boolean };
  receiving: { enabled: boolean; mxVerified: boolean };
  verification: {
    state: string;
    /** AUTHORITATIVE: the provider accepted the identity AND live DNS found the records. */
    dkimVerified: boolean;
    spfVerified: boolean;
    /** The provider's own verdict, so the UI can say which half is outstanding. */
    providerDkimVerified: boolean;
    providerSpfVerified: boolean;
    /** Our live public-DNS verdict. */
    dnsDkimVerified: boolean;
    dnsSpfVerified: boolean;
    mxVerified: boolean;
    receivingMxVerified: boolean;
    /** A DMARC record exists. Does NOT gate sending. */
    dmarcVerified: boolean;
    /** none | quarantine | reject. `none` publishes a record that enforces nothing. */
    dmarcPolicy: string | null;
    ownershipVerifiedAt: string | null;
    verifiedAt: string | null;
    lastCheckedAt: string | null;
    lastError: string | null;
    /** Which resolver answered, so a surprising verdict can be attributed. */
    resolver: string | null;
    checks: ApiDomainCheck[];
    /** When verification was last ATTEMPTED. */
    lastAttemptAt: string | null;
    /** When it last actually PASSED. Null if it never has. */
    lastSuccessAt: string | null;
  };
  /** Required records that are not yet passing — the exact records still needed. */
  outstandingRecords: ApiDomainCheck[];
  /** Records published with a wrong value, or conflicting. These never resolve themselves. */
  conflicts: ApiDomainCheck[];
  ownership: ApiDnsRecord | null;
  dnsRecords: ApiDnsRecord[];
  createdAt: string | null;
  updatedAt: string | null;
}

// ─── Automatic DNS configuration ─────────────────────────────────────────────

/**
 * A connected DNS provider.
 *
 * There is deliberately NO credential field. The server's serializer has none either, so this is a
 * shape that cannot carry one rather than one that merely happens not to.
 */
export interface ApiDnsConnection {
  id: number;
  provider: string;
  /** `api_token` or `oauth`. They differ in how the customer revokes them, so the UI says which. */
  credentialKind: string;
  grantedScopes: string[];
  providerAccountId: string | null;
  providerAccountName: string | null;
  /** active | invalid | revoked. `invalid` is the provider rejecting it, not the customer's doing. */
  status: string;
  statusDetail: string | null;
  lastUsedAt: string | null;
  lastError: string | null;
  createdAt: string | null;
}

export interface ApiDnsZone {
  id: string;
  /** The zone apex. Must contain the domain being configured, which the server enforces. */
  name: string;
  status: string;
}

/** One planned change. `action` is what would happen, not what did. */
export interface ApiDnsPlanRecord {
  id: string;
  kind: string;
  label: string;
  /**
   * create | update | noop | conflict.
   *
   * `conflict` is the one the customer must resolve — it means something is published that we will
   * not overwrite or remove, so it never resolves itself by waiting.
   */
  action: string;
  type: string;
  name: string;
  desired: string;
  /** What is published there now. Null when nothing is. */
  current: string | null;
  reason: string;
}

export interface ApiDnsPlan {
  domain: string;
  zone: { id: string; name: string };
  existingRecordsInZone: number;
  summary: { create: number; update: number; noop: number; conflict: number };
  /** False when any record is a conflict. The server refuses to apply an unsafe plan at all. */
  safe: boolean;
  /** Nothing to do — the idempotency signal. */
  alreadyConfigured: boolean;
  records: ApiDnsPlanRecord[];
}

export interface ApiDnsAppliedRecord {
  id: string;
  label: string;
  name: string;
  type: string;
  /** created | updated | unchanged | skipped | failed. `skipped` was never attempted. */
  outcome: string;
  providerRecordId: string | null;
  /** Cloudflare's CONFIRMED value, so "we set DNS only" is evidence rather than a claim. */
  proxied: boolean | null;
  detail: string;
}

export interface ApiDnsApplyResult {
  domain: string;
  zone: { id: string; name: string };
  complete: boolean;
  attempted: number;
  failed: number;
  records: ApiDnsAppliedRecord[];
  next: string;
}

/** One recorded verification attempt, from `GET /v1/domains/:id/attempts`. */
export interface ApiDomainAttempt {
  id: number;
  attemptedAt: string;
  /** customer | worker | staff | api | webhook */
  source: string;
  /** verified | pending | failed | provider_unavailable | provider_mismatch | error */
  outcome: string;
  ownershipVerified: boolean | null;
  providerDkimVerified: boolean | null;
  providerSpfVerified: boolean | null;
  dnsDkimVerified: boolean | null;
  dnsSpfVerified: boolean | null;
  dmarcVerified: boolean | null;
  providerStatus: string | null;
  resolver: string | null;
  error: string | null;
  durationMs: number | null;
}

/**
 * One row of the workspace's suppression list.
 *
 * `removable` is computed by the server, not by the client, and the UI must obey it rather than
 * re-deriving the rule from `reason`. A complaint cannot be removed by a tenant: it is a third party
 * saying we sent them spam, on a sending identity SHARED with every other workspace. The server
 * refuses with 403 `SUPPRESSION_PROTECTED`; this flag is what lets the UI say so BEFORE the click.
 */
export interface ApiSuppression {
  id: string;
  email: string;
  /** complaint | bounce | unsubscribe | manual */
  reason: string;
  detail: string | null;
  source: string;
  createdAt: string;
  removable: boolean;
}

export interface ApiSuppressionCheck {
  email: string;
  suppressed: boolean;
  /** `platform` for a platform-wide block, `workspace` for this workspace's own, null when clear. */
  scope: "platform" | "workspace" | null;
  reason: string | null;
}

// ─── Contacts / subscribers ──────────────────────────────────────────────────

/**
 * A subscriber's state. Closed on the server (CONTACT_STATUSES), so closed here.
 *
 * DERIVED from email_suppressions, never a stored column — which is why the UI shows it but never
 * offers to edit it as a field. The only way to change it is the subscription toggle, which writes a
 * suppression row, so the console and the dispatcher can never disagree about who may be mailed.
 */
export type ApiContactStatus = "subscribed" | "unsubscribed" | "bounced" | "suppressed";

export interface ApiContact {
  id: number;
  email: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  /** Arbitrary tenant-defined values, usable as template variables. */
  attributes: Record<string, string>;
  tags: string[];
  /** How this address arrived: manual | import | api | campaign_recipient | signup_form. */
  source: string;
  notes: string | null;
  status: ApiContactStatus;
  /** When the suppression behind a non-subscribed status was recorded; null when subscribed. */
  statusChangedAt: string | null;
  /** The suppression reason behind the status: bounce | complaint | unsubscribe | manual | null. */
  statusReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A list a contact belongs to, for the profile panel. */
export interface ApiContactListMembership {
  id: number;
  name: string;
  archivedAt: string | null;
}

export interface ApiContactImportRow {
  email: string;
  outcome: "created" | "updated" | "invalid";
  reason?: string;
}

export interface ApiContactImportResult {
  created: number;
  updated: number;
  invalid: number;
  /** Imported but not mailable (already unsubscribed/bounced/suppressed). */
  suppressed: number;
  rows: ApiContactImportRow[];
  total: number;
}

/** One entry on a contact's activity timeline. */
export interface ApiContactActivityEntry {
  kind: "send" | "event" | "campaign" | "enrollment";
  at: string;
  sendId: string | null;
  type: string;
  summary: string;
  detail: Record<string, unknown>;
}

export interface ApiContactActivity {
  entries: ApiContactActivityEntry[];
  nextBefore: string | null;
  pageSize: number;
  summary: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    bounced: number;
    complained: number;
  };
}

// ─── Lists ──────────────────────────────────────────────────────────────────

export interface ApiEmailList {
  id: number;
  name: string;
  description: string | null;
  /** Everyone on the list. */
  memberCount: number;
  /** Of those, how many can actually be mailed (subscribed). */
  sendableCount: number;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

/** Result of adding/removing list members. `unknownContactIds` are ids not in this workspace. */
export interface ApiListMembershipChange {
  added?: number;
  removed?: number;
  unknownContactIds: number[];
  memberCount: number;
}

// ─── Segments ─────────────────────────────────────────────────────────────────

export interface ApiSegmentRule {
  field: string;
  op: string;
  value?: string | number;
  /** Only for `field: "attribute"`. */
  key?: string;
}

export interface ApiSegmentDefinition {
  match: "all" | "any";
  rules: ApiSegmentRule[];
}

export interface ApiEmailSegment {
  id: number;
  name: string;
  description: string | null;
  definition: ApiSegmentDefinition;
  /** Contacts matching the rules right now. */
  matchCount: number;
  /** Of those, how many are sendable. */
  sendableCount: number;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

/** One selectable field in the segment builder, from GET /email/segments/fields. */
export interface ApiSegmentField {
  field: string;
  ops: string[];
  /** True for `attribute`, which needs a key alongside the value. */
  needsKey: boolean;
  /** Operators that take no value (is_set, opened, …). */
  valuelessOps: string[];
}

// ─── Audiences ─────────────────────────────────────────────────────────────────

/** How a campaign or automation chooses its recipients from the contact store. */
export type ApiAudienceSource =
  | { kind: "all" }
  | { kind: "list"; id: number }
  | { kind: "segment"; id: number }
  | { kind: "contacts"; contactIds: number[] };

export interface ApiAudienceDescription {
  kind: "all" | "list" | "segment" | "contacts";
  id: number | null;
  label: string;
  /** Everyone the audience resolves to. */
  total: number;
  /** Of those, how many can be mailed. */
  sendable: number;
}

export interface ApiAudienceAttachResult {
  audience: { kind: string; id: number | null; label: string };
  matched: number;
  added: number;
  excluded: number;
  recipientsTotal: number;
}

export interface ApiAudienceEnrollResult {
  audience: { kind: string; id: number | null; label: string };
  matched: number;
  enrolled: number;
  alreadyEnrolled: number;
  excluded: number;
  failures: Array<{ email: string; reason: string }>;
}

// ─── Templates ───────────────────────────────────────────────────────────────

/** draft | active | archived. Closed on the server (`STATUSES`), so it is closed here too. */
export type ApiTemplateStatus = "draft" | "active" | "archived";

/**
 * A template's METADATA. Its content lives on versions, never here.
 *
 * `currentVersionId` is the version a send resolves to when it names the template without pinning a
 * version. It is nullable in the type because the column is, though `createTemplate` writes version 1
 * in the same transaction — so in practice only a corrupted row has none, and the UI says so rather
 * than rendering a blank.
 */
export interface ApiTemplate {
  id: number;
  workspaceId: number;
  slug: string;
  name: string;
  description: string | null;
  status: ApiTemplateStatus;
  currentVersionId: number | null;
  currentVersionNo: number | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * One immutable version.
 *
 * `variables` is DERIVED by the server from the subject and bodies, not supplied by the caller. That
 * is why the editor here does not offer a variables field: it would be a second, disagreeing source
 * for something the renderer computes.
 */
export interface ApiTemplateVersion {
  id: number;
  templateId: number;
  workspaceId: number;
  versionNo: number;
  subject: string;
  htmlBody: string | null;
  textBody: string | null;
  variables: string[];
  createdBy: number | null;
  createdAt: string;
}

/** What `POST .../preview` renders. `html` and `text` are null when that body is not set. */
export interface ApiTemplatePreview {
  templateId: number;
  versionId: number;
  versionNo: number;
  subject: string;
  html: string | null;
  text: string | null;
  variables: string[];
}

/** The content half of a template, shared by create and by appending a version. */
export interface TemplateContent {
  subject: string;
  htmlBody?: string | null;
  textBody?: string | null;
}

// ─── Campaigns ───────────────────────────────────────────────────────────────

/**
 * The campaign state machine, closed on the server.
 *
 * The transitions matter more than the names, because the UI has to gate on them rather than
 * discover them through 409s:
 *   draft     → editable, recipients changeable, schedulable, sendable, cancelable
 *   scheduled → sendable (early), cancelable. NOT editable
 *   sending   → nothing. It is running
 *   sent / canceled / failed → terminal
 */
export type ApiCampaignStatus = "draft" | "scheduled" | "sending" | "sent" | "canceled" | "failed";

/**
 * `templateVersionId` is pinned at CREATION and never moves.
 *
 * That is the whole reason a campaign is reproducible: editing the template afterwards, even
 * appending a new current version, cannot change what an already-created campaign will send.
 *
 * The four counters are the send's own account of itself — `recipientsTotal` is the list size, and
 * sent + failed + skipped is progress. `skipped` is not a failure: a suppressed address is skipped
 * deliberately.
 */
export interface ApiCampaign {
  id: number;
  workspaceId: number;
  name: string;
  status: ApiCampaignStatus;
  templateId: number;
  templateVersionId: number;
  fromSendingAddressId: number | null;
  scheduledAt: string | null;
  recipientsTotal: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  createdAt: string;
  updatedAt: string;
}

/** One recipient. `variables` holds only the values this template actually declares. */
export interface ApiCampaignRecipient {
  id: number;
  email: string;
  variables: Record<string, string>;
  status: string;
}

// ─── Sequences (automations) ─────────────────────────────────────────────────

/**
 * The sequence lifecycle, closed on the server.
 *
 *   draft    → editable, steps changeable, activatable
 *   active   → accepts enrolments and sends. NOT editable — pause first
 *   paused   → editable again, keeps everyone's position, resumable
 *   archived → terminal. Everyone still walking was explicitly exited
 *
 * `paused` versus `archived` is the distinction worth having both for: pausing keeps positions so the
 * programme resumes where it left off, which is what you want when something looks wrong.
 */
export type ApiSequenceStatus = "draft" | "active" | "paused" | "archived";

/** Where one person is on their journey. */
export type ApiEnrollmentStatus = "active" | "completed" | "exited" | "failed";

// ─── API keys ─────────────────────────────────────────────────────────────────

/** A key as listed. Never carries the secret — only its prefix, for recognition. */
export interface ApiDeveloperKey {
  id: number;
  name: string;
  /** e.g. "pa_live_a1b2c3" — enough to recognise a key, not enough to use it. */
  keyPrefix: string;
  keyType: "live" | "test";
  revoked: boolean;
  lastUsedAt: string | null;
  createdAt: string;
}

/** The one-time creation response. `secret` is shown once and never retrievable again. */
export interface ApiDeveloperKeyCreated {
  id: number;
  name: string;
  keyPrefix: string;
  keyType: "live" | "test";
  /** The full key. Displayed once; the server stores only a hash. */
  secret: string;
}

/**
 * The Email events an endpoint can subscribe to — the closed vocabulary the server validates against
 * (lib/email/email-event-webhooks.ts EMAIL_WEBHOOK_EVENT_NAMES). Kept here as the source for the
 * event picker so the UI cannot offer a name the server would reject.
 */
export const EMAIL_WEBHOOK_EVENTS = [
  { event: "email.sent",         label: "Sent",         hint: "Accepted for delivery by the provider." },
  { event: "email.delivered",    label: "Delivered",    hint: "Confirmed delivered to the recipient's server." },
  { event: "email.bounced",      label: "Bounced",      hint: "Rejected — hard or soft bounce." },
  { event: "email.complained",   label: "Complained",   hint: "Marked as spam by the recipient." },
  { event: "email.opened",       label: "Opened",       hint: "The recipient opened the message." },
  { event: "email.clicked",      label: "Clicked",      hint: "The recipient clicked a tracked link." },
  { event: "email.unsubscribed", label: "Unsubscribed", hint: "The recipient unsubscribed." },
] as const;

/** A webhook endpoint as listed. `secretPrefix` recognises it; the full secret is shown only once. */
export interface ApiWebhookEndpoint {
  id: number;
  url: string;
  description: string | null;
  /** The event names this endpoint receives. */
  events: string[];
  active: boolean;
  /** e.g. "whsec_a1b2c3" — enough to recognise, never enough to forge a signature. */
  secretPrefix: string;
  createdAt: string;
}

/** The one-time creation response. `secret` (whsec_…) is shown once and never retrievable again. */
export interface ApiWebhookEndpointCreated extends ApiWebhookEndpoint {
  secret: string;
}

// ─── Settings ────────────────────────────────────────────────────────────────

/** The signed-in user's profile. `email` and `phone` are read-only by design. */
/** A workspace member, as `/api/team` reports them. */
export interface ApiTeamMember {
  id: number;
  name: string | null;
  email: string;
  role: string;
  status: string;
  /**
   * The member's EFFECTIVE permissions — the role ceiling intersected with any explicit grant,
   * resolved server-side. A client must not do that arithmetic itself; two implementations of an
   * authorisation rule disagree eventually, and the first disagreement is a privilege bug.
   */
  permissions: string[];
}

export interface ApiPermissionDefinition {
  key: string;
  label: string;
  group: string;
  description: string;
}

export interface ApiProfile {
  id: number;
  /** The customer-facing account id. Shown instead of the serial `id`, which would leak account order. */
  publicAccountId: string | null;
  name: string;
  email: string;
  phone: string | null;
  role: string;
  region: string | null;
  currency: string;
  businessType: string;
  emailVerified: boolean;
  createdAt: string;
  jobTitle: string | null;
  timezone: string;
  avatarUrl: string | null;
}

/** One active session (an unexpired refresh token) — the device list. */
export interface ApiAccountSession {
  id: number | string;
  family: string | null;
  createdAt: string;
  expiresAt: string;
  userAgent: string | null;
  ipAddress: string | null;
}

/**
 * The workspace row. Only `name` is editable from this console; `version` must be echoed back on a
 * PATCH so a concurrent edit is refused (409) rather than silently overwritten.
 */
export interface ApiWorkspace {
  id: number;
  name: string | null;
  slug: string | null;
  publicWorkspaceId: string | null;
  industry: string | null;
  planTier: string | null;
  sandboxMode: boolean | null;
  version?: number;
  createdAt?: string;
}

/** Sandbox mode: while on, only these recipients receive mail; every other send is blocked. */
export interface ApiSandbox {
  sandboxMode: boolean;
  sandboxRecipients: string[];
}

/** One delivery attempt record, for the delivery-history view. */
export interface ApiWebhookDelivery {
  id: string;
  endpointUrl: string;
  eventType: string;
  status: string;
  attemptCount: number;
  lastResponseStatus: number | null;
  lastError: string | null;
  deliveredAt: string | null;
  deadLetteredAt: string | null;
  createdAt: string;
}

// ─── Plans ───────────────────────────────────────────────────────────────────

/**
 * One plan, exactly as `email_product_plans` declares it.
 *
 * A `null` limit means NO FIXED LIMIT, not zero — the server converts its internal -1 sentinel to
 * null precisely so a client cannot render "-1 domains". An explicit 0 survives as 0, because a plan
 * may genuinely include none of something.
 */
/** A price in the customer's own currency, formatted server-side. */
export interface ApiLocalPrice {
  amount: number;
  currency: string;
  symbol: string;
  /** Ready to render, e.g. "₦24,000". Preformatted so no client picks its own decimals. */
  display: string;
}

export interface ApiEmailPlan {
  planKey: string;
  displayName: string;
  /** Integer USD cents. null = Contact Sales, never free. */
  priceUsdCents: number | null;
  /**
   * The CONFIGURATION price, always USD — the figure staff set and every local price is derived
   * from. Do NOT render this: a customer in Lagos is charged naira, and quoting them dollars is the
   * divergence `price` below exists to prevent.
   */
  priceUsd: number | null;
  /** @deprecated Always "USD". Use `price.currency`. */
  currency: string;
  billingInterval: string;

  /**
   * What THIS customer will be charged, in the currency they will be charged in, read server-side
   * from the authoritative price record. null for free and contact-sales plans.
   */
  price: ApiLocalPrice | null;
  /** The annual equivalent, when one is configured. */
  priceAnnual: ApiLocalPrice | null;
  /**
   * False when this region's currency has no configured price. Checkout refuses rather than
   * converting, so the card must say "not available in your currency" instead of quoting one.
   */
  priced: boolean;
  contactSales: boolean;
  mostPopular: boolean;
  sortOrder: number;
  isActive: boolean;
  limits: ApiEmailPlanLimits;
  capabilities: Record<string, boolean>;
}

/**
 * A plan's allowances, or — on `current.limits` — the allowances actually in force.
 *
 * Named so the two can share one shape. `current.limits` is the resolved set, which a prepaid volume
 * raises above whatever the catalogue tier says.
 */
export interface ApiEmailPlanLimits {
    emailsPerMonth: number | null;
    emailsPerDay: number | null;
    verifiedDomains: number | null;
    sendingAddresses: number | null;
    mailboxes: number | null;
    teamMembers: number | null;
    retentionDays: number | null;
    /**
     * Active outbound webhook endpoints. 0 = webhooks not included (Free/Starter),
     * N > 0 = included with N endpoints, null = no fixed limit (Enterprise). The pricing
     * card derives webhook availability from this, not a capability flag.
     */
    webhookEndpoints: number | null;
    /**
     * Email AI credits included per billing period. Spent only by AI-powered Email operations,
     * never by ordinary sending. 0 = none included, null = no fixed limit (Enterprise).
     */
    aiCredits: number | null;
    /** Automation (sequence step) runs included per billing period. */
    automationRuns: number | null;
    apiRateTier: string;
}

/**
 * Usage-based terms that apply ON TOP of a plan, served by `/v1/email/plans`.
 *
 * Read from the API rather than restated in the UI: a price hardcoded in the frontend is a second
 * source of truth, and the failure mode is the customer seeing one number and being charged another.
 */
export interface ApiUsageBasedPricing {
  email: {
    /** Emails per billable block beyond the included monthly allowance. */
    unitEmails: number;
    unitPriceUsdCents: number;
    unitPriceUsd: number;
    /** @deprecated The configuration currency. Use `unitPrice.currency`. */
    currency: string;
    /**
     * The overage unit in the customer's currency. null = not collectable in this currency yet, so
     * the configurator must not quote an overage figure it cannot charge.
     */
    unitPrice: ApiLocalPrice | null;
    rounding: string;
  };
  /**
   * The monthly spend at which the configurator stops quoting and recommends Enterprise.
   *
   * A recommendation threshold, not a cap — nothing refuses a customer above it. Served rather than
   * hardcoded here so the console has no commercial number of its own.
   */
  enterpriseThresholdUsdCents: number;
  enterpriseThresholdUsd: number;
  /**
   * The prepaid VOLUME terms. null when no tier can act as a base, i.e. nothing is purchasable.
   *
   * Everything needed to price a rung, so this file computes no commercial number of its own. The amount
   * is `basePrice + n x topUpPrice` from two CONFIGURED local prices — never an FX conversion — and the
   * server recomputes it authoritatively at checkout.
   */
  volume: ApiEmailVolumeTerms | null;
}

export interface ApiEmailVolumeTerms {
  /**
   * Every paid tier a rung can build on, cheapest first.
   *
   * The slider fills the gaps BETWEEN plans, so a rung builds on the largest tier it exceeds — 70,000 on
   * Growth, 250,000 on Business. Served rather than derived so the client never invents a base or a price.
   */
  basePlans: Array<{
    planKey: string;
    displayName: string;
    includedEmails: number;
    priceUsdCents: number;
    price: ApiLocalPrice | null;
  }>;
  /** The LARGEST paid tier. Retained for copy that names the top plan. */
  basePlanKey: string;
  basePlanDisplayName: string;
  baseIncludedEmails: number;
  /** One top-up: this many extra emails a month. */
  topUpEmails: number;
  topUpPriceUsdCents: number;
  topUpPriceUsd: number;
  /** The top-up in the customer's own currency. null = not purchasable in this region yet. */
  topUpPrice: ApiLocalPrice | null;
  /** True only when BOTH the base plan and the top-up are priced here, which checkout requires. */
  purchasable: boolean;
  /** At or above this volume there is no self-serve purchase — it is a contract. */
  enterpriseThresholdEmails: number;
  /** Every this much of top-up SPEND widens the non-mail ceilings by `upliftFraction`. */
  upliftPerUsdCents: number;
  upliftPerUsd: number;
  upliftFraction: number;
  upliftKeys: string[];
  /**
   * The purchasable volumes, derived SERVER-side.
   *
   * Why it is not computed here: the first rung is a whole top-up above the largest tier allowance, which
   * is what keeps the slider off 50,000 and 150,000 — Growth's and Business's own allowances, where a
   * top-up price would be higher than simply buying the plan. Deriving that in the browser would be a
   * second implementation of a commercial rule, and the checkout would refuse the rungs it got wrong.
   */
  ladder: number[];
}

/** A prepaid volume held by this workspace. Reported even once EXPIRED — see `state`. */
export interface ApiEmailVolume {
  /**
   * `active` while the prepaid month is in force, `expired` once it lapses.
   *
   * There is no auto-renewal on this platform, so `expired` is the normal path rather than an edge case,
   * and it must be SHOWN. Silently falling back to the tier's allowance would downgrade somebody who paid,
   * with nothing on screen explaining why their ceiling moved.
   */
  state: "none" | "active" | "expired";
  emailsPerMonth: number;
  topUps: number;
  /** Absolute allowances the purchase confers, frozen at purchase so later pricing changes cannot move them. */
  limits: {
    emailsPerMonth: number;
    verifiedDomains?: number;
    sendingAddresses?: number;
    mailboxes?: number;
    teamMembers?: number;
    webhookEndpoints?: number;
    automationRuns?: number;
    aiCredits?: number;
  };
  periodStart: string;
  periodEnd: string;
  basePlanKey: string | null;
}

/**
 * Automation runs this period. `includedLimit: null` is per-contract, never zero.
 *
 * `overage` is TRACKED, not blocked: a drip campaign that crosses the included allowance keeps
 * running, because stopping a customer's sequences mid-flight to protect a meter is worse than
 * billing them for the runs.
 */
export interface ApiAutomationUsage {
  includedLimit: number | null;
  used: number;
  remaining: number | null;
  overage: number;
  periodStart: string;
  resetsAt: string;
}

/** AI credits this period. `limit: null` is per-contract; `remaining: 0` genuinely refuses AI calls. */
export interface ApiAiCreditBalance {
  limit: number | null;
  used: number;
  remaining: number | null;
  periodStart: string;
  resetsAt: string;
}

/**
 * One AI credit movement. `credits` is what the operation cost.
 *
 * `status` distinguishes a charge that succeeded from one that failed and was refunded — a failed AI call
 * must not quietly keep the customer's credits, and showing only the balance hid whether it had.
 */
export interface ApiAiCreditEvent {
  id: string;
  operation: string;
  credits: number;
  status: string;
  model: string | null;
  failureReason: string | null;
  createdAt: string;
}

/** What one AI operation costs. Costs differ per operation — drafting is not classification. */
export interface ApiAiOperationCost {
  operation: string;
  label: string;
  credits: number;
}

/** The open month's projected email overage. Projected, not owed — the month is still running. */
export interface ApiProjectedOverage {
  includedEmails: number | null;
  usedEmails: number;
  overageEmails: number;
  billableBlocks: number;
  amountUsdCents: number;
  periodStart: string;
  unitEmails: number;
  unitPriceUsdCents: number;
  projected: boolean;
}

/**
 * One closed billing period.
 *
 * `dormant` is neither a failure nor a success: the amount is computed and owed, but collection is
 * waiting on gateway configuration. It is shown to the customer with its reason rather than hidden,
 * because the alternative is that four months of overage arrive as one surprise charge.
 */
export interface ApiOveragePeriod {
  id: string;
  workspaceId: number;
  periodStart: string;
  planKey: string | null;
  includedEmails: number | null;
  usedEmails: number;
  overageEmails: number;
  billableBlocks: number;
  amountUsdCents: number;
  status: "pending" | "dormant" | "awaiting_payment" | "charged" | "failed" | "waived";
  statusDetail: string | null;
  txRef: string | null;
  checkoutUrl: string | null;
  attempts: number;
  lastError: string | null;
}

export interface ApiEmailSubscription {
  planKey: string | null;
  /** The caller's own plan row, present even when it has been withdrawn from sale. */
  plan: ApiEmailPlan | null;
  status: string | null;
  /**
   * Whether the subscription confers usage RIGHT NOW. This is the field every server-side
   * enforcement site gates on, so it is the one the UI must believe.
   */
  entitled: boolean;
  /** A row exists. A cancelled or lapsed subscription is still `subscribed`. */
  subscribed: boolean;
  reason: string | null;
  gracePeriodEnd: string | null;
  /**
   * The allowances actually IN FORCE, after any prepaid volume is applied.
   *
   * Distinct from `plan.limits`, which are the catalogue tier's figures. A customer holding a volume
   * purchase is entitled to more than their tier says, and rendering the plan row would tell somebody who
   * bought 300,000 that they have 150,000.
   */
  limits: ApiEmailPlanLimits | null;
  /** The prepaid volume on this subscription, active or expired. null when never purchased. */
  volume: ApiEmailVolume | null;
}

export interface ApiSequence {
  id: number;
  workspaceId: number;
  name: string;
  slug: string;
  status: ApiSequenceStatus;
  fromSendingAddressId: number | null;
  enrolledCount: number;
  completedCount: number;
  exitedCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * One step. `templateVersionId` is pinned when the step is added, so revising the template cannot
 * change what someone mid-journey receives.
 *
 * `delaySeconds` is the wait BEFORE this step, measured from the previous step's send — or from
 * enrolment for step 1. So a 0/86400/259200 sequence sends immediately, then after a day, then two days
 * after that: four days total, not three.
 */
export interface ApiSequenceStep {
  id: number;
  workspaceId: number;
  sequenceId: number;
  stepNo: number;
  templateId: number;
  templateVersionId: number;
  delaySeconds: number;
}

export interface ApiEnrollment {
  id: number;
  workspaceId: number;
  sequenceId: number;
  email: string;
  variables: Record<string, string>;
  status: ApiEnrollmentStatus;
  nextStepNo: number;
  nextStepAt: string | null;
  lastStepNo: number | null;
  lastSentAt: string | null;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  exitReason: string | null;
  createdAt: string;
}

// ─── The workspace event feed ────────────────────────────────────────────────

/**
 * Every public event name the platform can produce, in the order a message travels.
 *
 * Mirrors `EMAIL_WEBHOOK_EVENT_NAMES` on the server. Note what is NOT here: `deferred`, `rejected` and
 * `unknown` have no public name and never appear in the feed — a failure's explanation lives on the
 * send's own status and errorDetail rather than in a half-named event. A screen that quietly dropped
 * them without saying so would leave someone counting events that do not add up.
 */
export const EMAIL_EVENT_NAMES = [
  "email.sent",
  "email.delivered",
  "email.bounced",
  "email.complained",
  "email.opened",
  "email.clicked",
  "email.unsubscribed",
] as const;
export type ApiEmailEventName = (typeof EMAIL_EVENT_NAMES)[number];

/** One event in the workspace-wide feed. `sendId` is what makes it traceable back to a message. */
export interface ApiWorkspaceEvent {
  type: string;
  occurredAt: string;
  data: Record<string, unknown>;
  sendId: string | null;
}

// ─── Inbound routing rules ───────────────────────────────────────────────────

/**
 * What a rule does. Closed vocabulary, mirrored by a database CHECK — a rule cannot delete, reject or
 * forward mail, and the API says so rather than just refusing.
 *
 * `hold` is the consequential one: the message is STORED but no ticket, acknowledgement or AI reply is
 * raised. It is the same outcome as a quarantine and never a deletion — but on an address the AI
 * product also serves, a hold rule is how support mail goes quiet without anything looking broken.
 */
export type ApiRoutingAction = "deliver" | "assign_queue" | "add_tag" | "hold";

/** Conditions this API version understands. Anything else is REFUSED, not ignored. */
export const ROUTING_CONDITION_KEYS = ["from", "fromDomain", "subject", "toLocalPart", "hasAttachment"] as const;
export type ApiRoutingConditionKey = (typeof ROUTING_CONDITION_KEYS)[number];

export interface ApiRoutingRule {
  id: number;
  workspaceId: number;
  /** null = applies to every mailbox in the workspace. */
  mailboxId: number | null;
  name: string;
  /** Evaluated ascending, then by id. Lower runs first. */
  priority: number;
  conditions: Record<string, unknown>;
  action: ApiRoutingAction;
  actionConfig: Record<string, unknown>;
  /** Stop evaluating further rules once this one matches. */
  stopProcessing: boolean;
  isActive: boolean;
}

// ─── Addresses (sending addresses and mailboxes) ─────────────────────────────

/**
 * One resource, two kinds — the same row shape with different purposes:
 *   `sending_address` — an address mail goes OUT from
 *   `mailbox`         — an address mail comes IN to
 *
 * Exposed by the API as `?kind=` rather than two parallel resources, so there is one shape to learn.
 * `kind` is REQUIRED on write: creating an inbound mailbox when an outbound sending address was meant
 * is a mistake worth refusing rather than defaulting through.
 */
export type ApiAddressKind = "sending_address" | "mailbox";

/**
 * The plan ceiling and what is left of it.
 *
 * `limit: null` means no ceiling — not zero, the same convention `/v1/email/usage` uses. Reading this
 * can fail with a 400 all of its own: the server resolves it from the Email subscription and FAILS
 * CLOSED when there is none, because these are billed resources and "we could not check" must never
 * mean "go ahead".
 */
export interface ApiAddressAllowance {
  kind: ApiAddressKind;
  used: number;
  limit: number | null;
  remaining: number | null;
  source: "email_product_plan" | "email_product_unlimited";
}

/** A verified address a campaign can send FROM. Without one, a campaign can be built but never sent. */
export interface ApiSendingAddress {
  id: number;
  workspaceId: number;
  tenantDomainId: number;
  localPart: string;
  domain: string;
  address: string;
  displayName: string | null;
  isActive: boolean;
}

export interface EnterpriseRequestInput {
  productKey: "email";
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  companyWebsite?: string;
  country?: string;
  industry?: string;
  expectedMonthlyEmails?: number;
  expectedDomains?: number;
  expectedSeats?: number;
  dataRetentionDays?: number;
  requirements?: string;
  interestedFeatures?: string[];
}

/**
 * One announcement banner, as the read endpoint serves it. `level` drives colour and
 * icon; `revision` bumps to bring a dismissed banner back when its wording changes.
 * `products` is not returned here — the server already filtered to this product.
 */
export interface ApiAnnouncement {
  id: number;
  level: "info" | "warning" | "error";
  message: string;
  actionLabel: string | null;
  actionUrl: string | null;
  revision: number;
  dismissible: boolean;
}

export const api = {
  /**
   * Ask for Enterprise Email — a tracked request, reviewed by staff.
   *
   * Replaces a `mailto:` link. `productKey` is what routes it to the Email applier on approval; without it the
   * server would default the request to the AI product, and approving it would grant the wrong thing entirely.
   *
   * The `expected*` figures are the customer's expectation and are never applied as limits: the enforced
   * numbers are written by staff at approval from the contract.
   */
  /**
   * Send a message to the team — the Help page's feedback form.
   *
   * Reuses the AI product's contact route rather than adding an Email-specific one. It already resolves
   * the caller's workspace, plan and support tier server-side, emails the team, and sets `reply-to` to
   * the sender so an answer lands in the customer's own inbox. A second endpoint would have to
   * rediscover all of that, and would drift from it.
   *
   * `subject` carries the category. It is the field the receiving inbox can sort and filter on, which a
   * line buried in the body is not.
   */
  sendSupportMessage: (input: { subject: string; message: string }) =>
    request<{ ok: boolean; routed?: string }>("/api/support/contact", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /**
   * Platform announcement banners for THIS product.
   *
   * The same `announcements` table and composer that drive the banner on the AI dashboard —
   * one place staff post a notice, both products can receive it. `?product=email` is what
   * scopes it: the server returns only banners whose product set includes email (or is
   * unset = all), so an AI-only notice never reaches this console. Session-authed on the
   * legacy `/api/*` surface because the read is audience-filtered per signed-in user.
   */
  announcements: () =>
    request<{ announcements: ApiAnnouncement[] }>("/api/announcements?product=email"),

  requestEnterprise: (input: EnterpriseRequestInput) =>
    request<{ request: { id: number; status: string } }>("/api/enterprise/activation-requests", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /**
   * A page of sends, newest first.
   *
   * Keyset paging: pass the previous response's `nextCursor` back as `cursor`. Not offset, because
   * this table is written to continuously and an offset over a time-ordered list skips and repeats
   * rows as new sends arrive — on the one screen used to reconcile what was actually sent.
   */
  sends: (params: ListSendsParams = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.cursor) q.set("cursor", params.cursor);
    if (params.status?.length) q.set("status", params.status.join(","));
    if (params.search) q.set("search", params.search);
    if (params.since) q.set("since", params.since);
    const qs = q.toString();
    return request<{ sends: ApiSend[]; nextCursor: string | null; pageSize: number }>(
      `/v1/email/sends${qs ? `?${qs}` : ""}`,
    );
  },

  /** One send by id. */
  send: (id: string) => request<{ send: ApiSend }>(`/v1/email/sends/${encodeURIComponent(id)}`),

  /** One send by the caller's own Idempotency-Key — the path that matters when a response was lost. */
  sendByKey: (key: string) => request<{ send: ApiSend }>(`/v1/email/sends/by-key/${encodeURIComponent(key)}`),

  /** The delivery timeline for one send, oldest first. */
  sendEvents: (id: string, limit?: number) =>
    request<{ events: ApiSendEvent[]; pageSize: number }>(
      `/v1/email/sends/${encodeURIComponent(id)}/events${limit ? `?limit=${limit}` : ""}`,
    ),

  /**
   * The workspace-wide event feed, newest first.
   *
   * Newest-first, unlike `sendEvents`: a feed answers "what just happened", while one send's timeline
   * is read forwards to reconstruct a story.
   *
   * Keyset paged on an opaque `cursor`. A page may contain FEWER events than it examined, because
   * events with no public name are dropped server-side — so `nextCursor` is the only reliable signal
   * that more exist, and an empty page with a non-null cursor is normal rather than the end.
   */
  events: (params: { limit?: number; cursor?: string | null; since?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.cursor) q.set("cursor", params.cursor);
    if (params.since) q.set("since", params.since);
    const qs = q.toString();
    return request<{ events: ApiWorkspaceEvent[]; nextCursor: string | null; pageSize: number }>(
      `/v1/email/events${qs ? `?${qs}` : ""}`,
    );
  },

  usage: () =>
    request<{
      monthly: { used: number; limit: number | null; remaining: number | null };
      daily: { used: number; limit: number | null; remaining: number | null };
      plan: string | null;
      entitled: boolean;
      enforcement: "off" | "log_only" | "enforce";
    }>("/v1/email/usage"),

  /**
   * Automation runs consumed this period.
   *
   * A SEPARATE meter from email volume and from AI credits, and deliberately kept that way: an
   * automation that sends an email consumes one run AND one email, and collapsing the three into a
   * single "usage" figure would make it impossible for a customer to see which one they are running
   * out of.
   *
   * `ledger` is the same figure counted from the per-run rows rather than the pool counter. Published
   * alongside so a disagreement is visible to the customer instead of a corrupted counter being
   * believed silently.
   */
  automationUsage: () =>
    request<{
      automation: ApiAutomationUsage;
      ledger: { included: number; overage: number; total: number };
    }>("/v1/email/automation/usage"),

  /**
   * The AI credit balance, and what each AI operation costs.
   *
   * `operations` comes from the server so the console never hardcodes a credit cost — a number that
   * disagreed with what is actually charged is the worst kind of drift on a metered feature.
   */
  aiCredits: () =>
    request<{ credits: ApiAiCreditBalance; operations: ApiAiOperationCost[] }>("/v1/email/ai/credits"),

  /**
   * What the credits were actually spent ON, most recent first.
   *
   * The balance alone answers "how many are left" and never "where did they go", which is the first thing a
   * customer asks when credits run out — and without it the only honest answer support could give was to
   * read the database.
   *
   * `request`, not `developerRequest`: `/v1` accepts a session token as well as an API key
   * (`requireApiKeyAuth`), and a session carries `DEFAULT_API_KEY_SCOPES`, which includes `ai_tools`. So
   * this needed no new server route — the endpoint existed and simply had no caller.
   */
  aiCreditHistory: (limit = 50) =>
    request<{ items: ApiAiCreditEvent[]; total: number }>(
      `/v1/email/ai/credits/history?limit=${encodeURIComponent(String(limit))}`,
    ),

  /**
   * Email-volume pay-as-you-go: the current month PROJECTED, and every closed period.
   *
   * `current.projected` is true because the month is still running — presenting a mid-month figure as
   * owed would be wrong in both directions. Closed periods include `dormant` ones with their reason,
   * because a bill a customer cannot yet pay is still one they are entitled to see coming.
   */
  paygOverage: () =>
    request<{
      current: ApiProjectedOverage;
      rate: { unitEmails: number; unitPriceUsdCents: number; rounding: string };
      periods: ApiOveragePeriod[];
    }>("/v1/email/payg/overage"),

  // ─── Email plan purchase (SESSION routes on /api, never /v1) ──────────────
  //
  // On `/api` and not `/v1` deliberately: a `/v1` API key belongs to the workspace it would be buying
  // for, so a checkout endpoint there would let a key escalate its own entitlement. These are session
  // authenticated, and the purchase is owner-only server-side.
  //
  // Nothing here reports a payment RESULT. `startEmailCheckout` hands back a hosted-checkout URL, and
  // `emailCheckoutStatus` reads the server's own payment-intent row — the redirect's `?status=` is
  // never consulted, because a browser-supplied payment result is not evidence of payment.

  /** Which plans this customer can buy, and whether each is priced in their currency. */
  emailBillingOptions: () =>
    request<{
      gatewayConfigured: boolean;
      currency: string;
      options: Array<{
        planKey: string; displayName: string; priceUsdCents: number;
        priced: boolean; amountMinor: number | null; amountMajor: number | null;
      }>;
    }>("/api/email/billing/options"),

  /** Start a purchase. Returns a Flutterwave hosted-checkout URL to redirect to. */
  startEmailCheckout: (planKey: string) =>
    request<{
      checkoutUrl: string; txRef: string; planKey: string;
      currency: string; amount: number; billingInterval: string;
    }>("/api/email/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ planKey }),
    }),

  /**
   * Buy a prepaid monthly VOLUME — one month, no auto-renewal.
   *
   * Sends a volume and never an amount. The server derives the price from the configured base plan price
   * plus N configured top-up prices in this customer's currency, and re-validates the volume against the
   * live catalogue, so this call cannot name a price, a discount or a rung the rules refuse.
   */
  startEmailVolumeCheckout: (emails: number) =>
    request<{
      checkoutUrl: string; txRef: string; emails: number; topUps: number;
      basePlanKey: string; currency: string; amount: number;
      grantedLimits: Record<string, number>;
      uplift: { bands: number; fraction: number; multiplier: number; nextBandAtTopUps: number | null };
    }>("/api/email/billing/volume-checkout", {
      method: "POST",
      body: JSON.stringify({ emails }),
    }),

  /** Poll after returning from checkout — the webhook may not have landed yet. */
  emailCheckoutStatus: (txRef: string) =>
    request<{
      found: boolean; status: string | null; planKey: string | null;
      entitled: boolean; currentPlanKey: string | null; detail: string | null;
      /** `email_volume` for a prepaid volume purchase. */
      purpose: string | null;
      purchasedEmails: number | null;
      /** The allowance in force NOW — the resolved figure, so a purchase can be confirmed as real. */
      emailsPerMonth: number | null;
    }>(`/api/email/billing/checkout/${encodeURIComponent(txRef)}`),

  /** Put this workspace on the free plan. The plan cannot be named — that is the security property. */
  activateFreeEmailPlan: () =>
    request<{ subscription: { planKey: string; status: string }; outcome: string }>(
      "/api/email/subscription/free", { method: "POST" },
    ),

  analyticsOverview: (days?: number) =>
    request<ApiOverview>(`/v1/email/analytics/overview${days ? `?days=${days}` : ""}`),

  // ─── Configuration ─────────────────────────────────────────────────────────

  /**
   * The whole configuration chain, checklist and plan allowances in one read.
   *
   * A session route rather than `/v1`, because it composes several surfaces that are themselves
   * session-only (API keys, mailboxes) and because the causation between the links is the server's
   * to decide — see the endpoint's own header. Errors are RFC 7807, so `request` is right here.
   */
  emailConfiguration: (windowDays?: 7 | 30) =>
    request<ApiEmailConfiguration>(
      `/api/email/configuration${windowDays ? `?windowDays=${windowDays}` : ""}`,
    ),

  // ─── Domains ───────────────────────────────────────────────────────────────

  domains: () => request<{ domains: ApiDomain[]; total: number }>("/v1/domains"),

  createDomain: (domain: string) =>
    request<ApiDomain & { dnsInstructions?: unknown }>("/v1/domains", {
      method: "POST",
      body: JSON.stringify({ domain }),
    }),

  /**
   * Re-run the live DNS check.
   *
   * Slow by nature — it queries the provider and then resolves DNS — and it can legitimately answer
   * 503 (`RESEND_VERIFICATION_UNAVAILABLE`) without changing anything. The caller must show that as
   * "we could not check", never as "not verified": telling someone their DNS is wrong when we simply
   * could not reach the provider sends them to edit records that were already correct.
   */
  verifyDomain: (id: number) =>
    request<ApiDomain>(`/v1/domains/${id}/verify`, { method: "POST" }),

  deleteDomain: (id: number) => request<void>(`/v1/domains/${id}`, { method: "DELETE" }),

  /**
   * This domain's verification history.
   *
   * Answers what the domain row alone cannot: whether it ever passed, when it regressed, and
   * whether the last failure was the customer's DNS or our provider being unreachable. The
   * server bounds `limit` regardless of what is asked for.
   */
  domainAttempts: (id: number, limit = 20) =>
    request<{ domain: string; attempts: ApiDomainAttempt[]; total: number; limit: number }>(
      `/v1/domains/${id}/attempts?limit=${limit}`,
    ),

  // ─── Automatic DNS configuration ───────────────────────────────────────────
  //
  // Every one of these 404s unless the deployment has switched the integration on, which is why
  // `dnsConnection()` is called first and its `mode` drives whether any of this UI appears at all.
  // A 404 here means "not offered", not "broken".

  /** What is connected, and which credential model this deployment uses. */
  dnsConnection: () =>
    request<{ mode: string; requiredScopes: string[]; connection: ApiDnsConnection | null }>(
      "/v1/dns/connection",
    ),

  /** Token mode only. The server proves the credential before storing it. */
  connectDnsToken: (token: string) =>
    request<{ connection: ApiDnsConnection; zones: ApiDnsZone[] }>("/v1/dns/connection", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  /** OAuth mode only. Returns the URL to navigate the browser to; the redirect is not ours to make. */
  dnsOAuthAuthorize: () =>
    request<{ authorizeUrl: string; requiredScopes: string[] }>("/v1/dns/oauth/authorize", {
      method: "POST",
    }),

  disconnectDns: () =>
    request<{ disconnected: boolean; upstreamRevoked: boolean; note: string }>("/v1/dns/connection", {
      method: "DELETE",
    }),

  dnsZones: () => request<{ zones: ApiDnsZone[]; total: number }>("/v1/dns/zones"),

  /**
   * What WOULD change. Writes nothing.
   *
   * The returned plan is a preview only — `applyDomainDns` re-plans server-side rather than accepting
   * anything from here, so this cannot be edited into instructions for what to write.
   */
  planDomainDns: (domainId: number, zoneId: string) =>
    request<ApiDnsPlan>(`/v1/domains/${domainId}/dns/plan`, {
      method: "POST",
      body: JSON.stringify({ zoneId }),
    }),

  applyDomainDns: (domainId: number, zoneId: string) =>
    request<ApiDnsApplyResult>(`/v1/domains/${domainId}/dns/apply`, {
      method: "POST",
      body: JSON.stringify({ zoneId }),
    }),

  // ─── Suppressions ──────────────────────────────────────────────────────────

  suppressions: (params: { limit?: number; offset?: number; email?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    if (params.email) q.set("email", params.email);
    const qs = q.toString();
    return request<{ suppressions: ApiSuppression[]; pageSize: number; total: number }>(
      `/v1/email/suppressions${qs ? `?${qs}` : ""}`,
    );
  },

  /**
   * Will a send to this address be refused?
   *
   * The only suppression endpoint that consults PLATFORM-wide entries as well as the workspace's
   * own, so it agrees with what the dispatcher will actually do. A platform block comes back with
   * `scope: "platform"` and a null reason — the operator-written detail on those rows is
   * deliberately never exposed to a tenant.
   */
  checkSuppression: (email: string) =>
    request<ApiSuppressionCheck>(`/v1/email/suppressions/check?email=${encodeURIComponent(email)}`),

  addSuppression: (input: { email: string; reason: "unsubscribe" | "manual"; detail?: string }) =>
    request<{ suppression: { email: string; reason: string; scope: string }; outcome: "created" | "already_present" }>(
      "/v1/email/suppressions",
      { method: "POST", body: JSON.stringify(input) },
    ),

  removeSuppression: (id: string) =>
    request<{ id: string; email: string }>(`/v1/email/suppressions/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  // ─── Templates ─────────────────────────────────────────────────────────────
  //
  // The split between PATCH and POST .../versions is the whole model, and it is the server's rule
  // rather than a convention this client invented: metadata is mutable, content is append-only. That
  // is what makes a pinned `versionId` on a send a promise — renaming a template cannot alter what an
  // already-pinned version renders. The UI is built around the same split, so nothing here needs to
  // pretend content can be edited in place.

  templates: () => request<{ templates: ApiTemplate[] }>("/v1/email/templates"),

  /** One template with its FULL version history, newest first. One request, both halves. */
  template: (id: number) =>
    request<{ template: ApiTemplate; versions: ApiTemplateVersion[] }>(`/v1/email/templates/${id}`),

  /**
   * Create a template and its version 1 together.
   *
   * 201 returns both, and the version is worth keeping: its id is what a caller pins on a send.
   * A duplicate slug comes back 409 `CONFLICT` — slugs are unique per workspace and are how an
   * integration names a template without hard-coding a numeric id.
   */
  createTemplate: (input: TemplateContent & { slug: string; name: string; description?: string | null }) =>
    request<{ template: ApiTemplate; version: ApiTemplateVersion }>("/v1/email/templates", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /**
   * Metadata only — name, description, status. Never content.
   *
   * The server refuses an empty patch with 400, so callers must send at least one key. Sending
   * `description: null` clears it; omitting the key leaves it alone. That distinction is real, so this
   * takes the object as given rather than spreading defaults over it.
   */
  patchTemplate: (id: number, patch: { name?: string; description?: string | null; status?: ApiTemplateStatus }) =>
    request<{ template: ApiTemplate }>(`/v1/email/templates/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** Append a version and make it current. The only way to change what a template renders. */
  createTemplateVersion: (id: number, content: TemplateContent) =>
    request<{ version: ApiTemplateVersion }>(`/v1/email/templates/${id}/versions`, {
      method: "POST",
      body: JSON.stringify(content),
    }),

  /**
   * ARCHIVE. `DELETE` is the method because that is what a REST client reaches for, but nothing is
   * destroyed: the row and every version remain, so a send that pinned a version keeps resolving and
   * historical sends stay explainable. It simply becomes unselectable for new sends — which is why the
   * UI calls this "Archive" and never "Delete", and why an archived template is still readable here.
   */
  archiveTemplate: (id: number) =>
    request<{ id: number; status: "archived" }>(`/v1/email/templates/${id}`, { method: "DELETE" }),

  /**
   * Render a version without sending anything.
   *
   * POST despite being read-only, because the values to substitute belong in a body. Preview mode on
   * the server: an unsupplied variable falls back to its catalogue example instead of failing, since a
   * preview is not a send. Omit `versionId` to render whichever version is current.
   */
  previewTemplate: (id: number, input: { versionId?: number; variables?: Record<string, string> } = {}) =>
    request<ApiTemplatePreview>(`/v1/email/templates/${id}/preview`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  // ─── Campaigns ─────────────────────────────────────────────────────────────
  //
  // A CampaignStateError answers **409**, not 400: the request was well formed, the campaign has
  // simply moved on. That distinction is why the UI gates on `status` rather than letting people
  // discover the state machine by being refused — but it also handles the 409, because the campaign
  // can change between a page load and a click.

  campaigns: () => request<{ campaigns: ApiCampaign[] }>("/v1/email/campaigns"),

  campaign: (id: number) => request<{ campaign: ApiCampaign }>(`/v1/email/campaigns/${id}`),

  /**
   * Create a draft, pinning a template version immediately.
   *
   * `template` takes the same three shapes as `POST /email/send`: `{ slug }`, `{ templateId }`, or
   * `{ templateId, versionId }` to pin an exact version. Without `versionId` the server resolves the
   * template's CURRENT version and pins that — so "latest" is captured at creation, never followed.
   */
  createCampaign: (input: { name: string; template: { slug?: string; templateId?: number; versionId?: number } }) =>
    request<{ campaign: ApiCampaign }>("/v1/email/campaigns", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /** Draft only. Content is not patchable — it comes from the pinned version. */
  patchCampaign: (id: number, patch: { name?: string; fromSendingAddressId?: number | null; scheduledAt?: string | null }) =>
    request<{ campaign: ApiCampaign }>(`/v1/email/campaigns/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /**
   * A page of recipients, oldest first.
   *
   * KEYSET paging on `afterId`, not offset — deliberately, because this list is written to *during*
   * a send and an offset would skip and repeat rows on the one screen used to reconcile who was
   * actually mailed. Pass the previous response's `nextAfterId`; null means the end.
   */
  campaignRecipients: (id: number, params: { limit?: number; afterId?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.afterId) q.set("afterId", String(params.afterId));
    const qs = q.toString();
    return request<{ recipients: ApiCampaignRecipient[]; nextAfterId: string | null }>(
      `/v1/email/campaigns/${id}/recipients${qs ? `?${qs}` : ""}`,
    );
  },

  /**
   * Add or update recipients on a draft. Safe to retry: re-adding an address updates its variables
   * rather than duplicating it. The server validates every row against the pinned version's declared
   * variables and refuses the WHOLE batch on a bad one, so a half-loaded campaign is not reachable.
   */
  addCampaignRecipients: (id: number, recipients: Array<{ email: string; variables?: Record<string, string> }>) =>
    request<{ added: number; recipientsTotal: number }>(`/v1/email/campaigns/${id}/recipients`, {
      method: "POST",
      body: JSON.stringify({ recipients }),
    }),

  removeCampaignRecipient: (id: number, recipientId: number) =>
    request<{ id: string; removed: boolean }>(`/v1/email/campaigns/${id}/recipients/${recipientId}`, {
      method: "DELETE",
    }),

  /**
   * Schedule for a named time. `scheduledAt` is REQUIRED — the server refuses to default it and the
   * database forbids `scheduled` with a null time, because no sweeper can act on that correctly:
   * treating null as due-now sends a campaign nobody timed, and skipping it strands the campaign.
   *
   * Also needs a sending address and at least one recipient already set.
   */
  scheduleCampaign: (id: number, scheduledAt: string) =>
    request<{ campaign: ApiCampaign }>(`/v1/email/campaigns/${id}/schedule`, {
      method: "POST",
      body: JSON.stringify({ scheduledAt }),
    }),

  /** Send now — from draft or scheduled. 202: the fan-out is queued, delivery is reported by events. */
  sendCampaign: (id: number) =>
    request<{ campaign: ApiCampaign }>(`/v1/email/campaigns/${id}/send`, { method: "POST" }),

  /** Draft or scheduled only. A sending or sent campaign cannot be recalled. */
  cancelCampaign: (id: number) =>
    request<{ campaign: ApiCampaign }>(`/v1/email/campaigns/${id}/cancel`, { method: "POST" }),

  /**
   * Addresses a campaign can send from.
   *
   * `GET /v1/email/addresses` defaults to `sending_address`, which is what composing a campaign
   * needs. Scope is `domains` rather than `send`, so this can fail independently of the campaign
   * calls — worth handling separately rather than treating it as part of the campaign load.
   */
  sendingAddresses: () => api.addresses("sending_address"),

  // ─── Addresses ─────────────────────────────────────────────────────────────

  addresses: (kind: ApiAddressKind = "sending_address") =>
    request<{ kind: ApiAddressKind; addresses: ApiSendingAddress[] }>(`/v1/email/addresses?kind=${kind}`),

  /**
   * The ceiling, asked for rather than discovered by hitting it.
   *
   * Kept as its own call because it fails independently: with no active Email subscription the server
   * refuses this with a 400 while the LIST still returns fine. Folding the two together would report
   * "could not load addresses" for a workspace whose addresses loaded perfectly.
   */
  addressAllowance: (kind: ApiAddressKind = "sending_address") =>
    request<ApiAddressAllowance>(`/v1/email/addresses/allowance?kind=${kind}`),

  /**
   * Create an address on a VERIFIED domain owned by this workspace.
   *
   * Both halves are enforced server-side and neither is cosmetic: an address on an unverified domain
   * can neither send nor receive, and attaching one to another tenant's verified domain would let a
   * workspace send mail authenticating as them.
   *
   * A sending address additionally cannot use a reserved prefix (`postmaster`, `abuse`, `noreply` and
   * friends). A MAILBOX may — a tenant can legitimately receive on `noreply@`.
   */
  createAddress: (input: { kind: ApiAddressKind; domainId: number; localPart: string; displayName?: string | null }) =>
    request<{ kind: ApiAddressKind; address: ApiSendingAddress }>("/v1/email/addresses", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /**
   * DEACTIVATE, not delete. The row stays, so historical sends attributed to the address remain
   * explainable and an inbound mailbox's conversations do not orphan — while the plan allowance is
   * freed, which is what "remove" actually means to a customer.
   */
  deactivateAddress: (id: number, kind: ApiAddressKind) =>
    request<{ id: string; kind: ApiAddressKind; status: "inactive" }>(
      `/v1/email/addresses/${id}?kind=${kind}`,
      { method: "DELETE" },
    ),

  // ─── Inbound routing rules ─────────────────────────────────────────────────
  //
  // Returned in EVALUATION order (priority ascending, then id), so the list on screen is the order
  // the worker actually applies. Presenting them sorted any other way would misrepresent behaviour.

  /**
   * The plan catalogue, and where this workspace stands in it.
   *
   * Every price and every quota comes from `email_product_plans` — the same rows the admin panel
   * edits and the same rows the enforcement layer reads. Nothing here is hardcoded in the console, so
   * an administrator changing Growth's credits from 50,000 to 75,000 changes what this screen shows on
   * the next load, with no deploy.
   *
   * Unlike every other Email endpoint this one is NOT gated on a plan capability, deliberately: a
   * capability check would hide the price list from exactly the workspaces that need it — free tier,
   * lapsed, or never subscribed.
   */
  emailPlans: () =>
    request<{
      plans: ApiEmailPlan[];
      current: ApiEmailSubscription;
      usageBasedPricing: ApiUsageBasedPricing;
      /** The currency this customer pays in, resolved from their region by the server. */
      billing: {
        currency: string;
        symbol: string;
        detectedCountry: string | null;
        isConfigurationCurrency: boolean;
        configurationCurrency: string;
      };
    }>("/v1/email/plans"),

  // ─── Sequences (Automations) ─────────────────────────────────────────────
  //
  // The whole surface is gated on TWO capabilities server-side: `email_api` and `automation`. A plan
  // without `automation` answers 402 on every one of these while the rest of the console keeps
  // working, so the screen has to treat "you cannot use this" as a first-class state rather than as
  // an error — it is the expected answer on the free tier, not a fault.

  sequences: () => request<{ sequences: ApiSequence[] }>("/v1/email/sequences"),

  /** The detail read returns the steps alongside the sequence, so one request populates the drawer. */
  sequence: (id: number) =>
    request<{ sequence: ApiSequence; steps: ApiSequenceStep[] }>(`/v1/email/sequences/${id}`),

  createSequence: (input: { name: string; slug: string }) =>
    request<{ sequence: ApiSequence }>("/v1/email/sequences", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /**
   * Rename, or set the sending address.
   *
   * `fromSendingAddressId: null` CLEARS it — the server distinguishes an absent key ("leave alone")
   * from an explicit null, so this must not collapse the two.
   */
  updateSequence: (id: number, patch: { name?: string; fromSendingAddressId?: number | null }) =>
    request<{ sequence: ApiSequence }>(`/v1/email/sequences/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /**
   * Add a step, pinning a concrete template version at the moment it is added.
   *
   * `delaySeconds` is the wait BEFORE this step, measured from the previous step's send — or from
   * enrolment for step 1. Only permitted while `draft` or `paused`: adding a step to a live sequence
   * would rewrite the journey for everyone mid-walk, and whether a given person ever received the new
   * step would be decided purely by how far along they happened to be.
   */
  addSequenceStep: (
    id: number,
    input: { template: { slug?: string; templateId?: number; versionId?: number }; delaySeconds: number; stepNo?: number },
  ) =>
    request<{ step: ApiSequenceStep }>(`/v1/email/sequences/${id}/steps`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  removeSequenceStep: (id: number, stepNo: number) =>
    request<{ stepNo: number; removed: boolean }>(`/v1/email/sequences/${id}/steps/${stepNo}`, {
      method: "DELETE",
    }),

  activateSequence: (id: number) =>
    request<{ sequence: ApiSequence }>(`/v1/email/sequences/${id}/activate`, { method: "POST" }),

  pauseSequence: (id: number) =>
    request<{ sequence: ApiSequence }>(`/v1/email/sequences/${id}/pause`, { method: "POST" }),

  archiveSequence: (id: number) =>
    request<{ sequence: ApiSequence }>(`/v1/email/sequences/${id}/archive`, { method: "POST" }),

  /**
   * Enrol one recipient.
   *
   * Answers **200 with `skipped: "already_enrolled"`** for an address already mid-walk — not 201, and
   * not an error. A second active enrolment would send every remaining step twice, and a caller
   * re-enrolling is almost always a retry, so that is the state they wanted. The screen must report
   * it as "already in this sequence" rather than as a success or a failure, because it is neither.
   */
  enroll: (id: number, input: { email: string; variables?: Record<string, string> }) =>
    request<{ enrollment: ApiEnrollment | null; skipped: "already_enrolled" | null }>(
      `/v1/email/sequences/${id}/enrollments`,
      { method: "POST", body: JSON.stringify(input) },
    ),

  enrollments: (id: number, params: { limit?: number; afterId?: number; status?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.afterId) q.set("afterId", String(params.afterId));
    if (params.status) q.set("status", params.status);
    const qs = q.toString();
    return request<{ enrollments: ApiEnrollment[]; nextAfterId: string | null; pageSize: number }>(
      `/v1/email/sequences/${id}/enrollments${qs ? `?${qs}` : ""}`,
    );
  },

  /**
   * Stops the remaining steps for one person. Does not un-send what already went.
   *
   * Takes no reason: the server records `"manual"` unconditionally. An optional `reason` argument
   * here would be silently discarded, which is worse than not offering it — a caller would believe
   * they had annotated the exit.
   */
  exitEnrollment: (id: number, enrollmentId: number) =>
    request<{ enrollment: ApiEnrollment }>(
      `/v1/email/sequences/${id}/enrollments/${enrollmentId}`,
      { method: "DELETE" },
    ),

  routingRules: () => request<{ rules: ApiRoutingRule[] }>("/v1/email/routing-rules"),

  /**
   * Create a rule.
   *
   * `conditions` and `actionConfig` are validated as a PAIR with the action: `assign_queue` without a
   * queue does nothing, and a queue on a `hold` rule is meaningless, so the server refuses both rather
   * than accepting a rule that silently never works. An unknown condition key is refused for the same
   * reason — the evaluator would treat the rule as never matching, and a rule that appears to work for
   * a year is worse than one that was rejected on the day it was written.
   */
  createRoutingRule: (input: {
    name: string;
    action: ApiRoutingAction;
    conditions?: Record<string, unknown>;
    actionConfig?: Record<string, unknown>;
    mailboxId?: number | null;
    priority?: number;
    stopProcessing?: boolean;
  }) =>
    request<{ rule: ApiRoutingRule }>("/v1/email/routing-rules", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  patchRoutingRule: (
    id: number,
    patch: {
      name?: string;
      action?: ApiRoutingAction;
      conditions?: Record<string, unknown>;
      actionConfig?: Record<string, unknown>;
      mailboxId?: number | null;
      priority?: number;
      stopProcessing?: boolean;
      isActive?: boolean;
    },
  ) =>
    request<{ rule: ApiRoutingRule }>(`/v1/email/routing-rules/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** DEACTIVATE. The rule stays readable so a past routing decision remains explainable. */
  deactivateRoutingRule: (id: number) =>
    request<{ id: string; status: string }>(`/v1/email/routing-rules/${id}`, { method: "DELETE" }),

  // ─── API keys (session + owner authed; see developerRequest) ──────────────

  developerKeys: () => developerRequest<ApiDeveloperKey[]>("/api/developer/keys"),

  /**
   * Mint a key. The secret is returned ONCE, here, and never again — the server keeps only a hash.
   * `type: "test"` mints a test key; anything else mints a live one.
   */
  createDeveloperKey: (input: { name: string; type: "live" | "test" }) =>
    developerRequest<ApiDeveloperKeyCreated>("/api/developer/keys", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /** Roll a key: same row, new secret, old secret dead immediately. Returns the new secret once. */
  rollDeveloperKey: (id: number) =>
    developerRequest<{ id: number; keyPrefix: string; secret: string }>(
      `/api/developer/keys/${id}/roll`,
      { method: "POST" },
    ),

  /** Revoke a key. Irreversible — a revoked key cannot be un-revoked, only replaced. */
  revokeDeveloperKey: (id: number) =>
    developerRequest<{ revoked: boolean }>(`/api/developer/keys/${id}`, { method: "DELETE" }),

  // ─── Webhooks (session + owner authed; see developerRequest) ──────────────

  developerWebhooks: () => developerRequest<ApiWebhookEndpoint[]>("/api/developer/webhooks"),

  /**
   * Register an endpoint. The signing secret is returned ONCE, here, and never again — the server
   * keeps only a hash (plus an encrypted copy it uses itself to sign, never exposed).
   */
  createDeveloperWebhook: (input: { url: string; events: string[]; description?: string }) =>
    developerRequest<ApiWebhookEndpointCreated>("/api/developer/webhooks", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /** Delete an endpoint. Immediate — no further deliveries are attempted to it. */
  deleteDeveloperWebhook: (id: number) =>
    developerRequest<{ deleted: boolean }>(`/api/developer/webhooks/${id}`, { method: "DELETE" }),

  /** Fire a real, signed `test.ping` delivery so the customer can confirm their endpoint receives it. */
  testDeveloperWebhook: (id: number) =>
    developerRequest<{ fired: boolean; deliveryId?: string; eventType?: string; note?: string }>(
      `/api/developer/webhooks/${id}/test`,
      { method: "POST" },
    ),

  /** The recent delivery attempts across all of the workspace's endpoints. */
  developerWebhookDeliveries: () =>
    developerRequest<{ items: ApiWebhookDelivery[]; total: number }>("/api/developer/webhook-deliveries"),

  // ─── Settings (session authed; see developerRequest) ──────────────────────
  //
  // These are the FIRST-PARTY account/workspace endpoints, not part of the public `/v1` contract —
  // `/v1` deliberately exposes no way to read a profile, change a password or name a workspace,
  // because an API key must not be able to do any of those. They are session-only by design, so the
  // Settings screen is the one place besides API keys and webhooks that reaches `/api/*`.

  /** The signed-in user's extended profile. `email`/`phone` are read-only (see requestContactChange). */
  /*
   * ── Team ──────────────────────────────────────────────────────────────────
   *
   * The SAME endpoints the AI product uses. ENICE is one platform with one identity, so Email does not
   * get its own members table, its own invite mail or its own role vocabulary — it gets a screen onto
   * the workspace team that already exists, filtered to the Email permissions.
   *
   * Session-authed (`/api/*`), never API-key-authed: granting somebody authority over a workspace must
   * require a human session, exactly as minting a credential does.
   */
  team: () => developerRequest<{ members: ApiTeamMember[] }>("/api/team"),

  teamPermissions: () =>
    developerRequest<{
      permissions: ApiPermissionDefinition[];
      roleDefaults: Record<string, string[]>;
      /** Roles the CALLER may hand out — the server refuses anything above its own rank. */
      assignableRoles: string[];
      callerRole: string;
      callerPermissions: string[];
    }>("/api/team/permissions"),

  inviteTeamMember: (body: { name: string; email: string; role: string }) =>
    developerRequest<{ member?: ApiTeamMember; message?: string }>("/api/team", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  updateTeamMember: (id: number, body: { role?: string; status?: string; isActive?: boolean }) =>
    developerRequest<{ member?: ApiTeamMember }>(`/api/team/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  removeTeamMember: (id: number) =>
    developerRequest<{ removed?: boolean; message?: string }>(`/api/team/${id}`, { method: "DELETE" }),

  profile: () => developerRequest<ApiProfile>("/api/auth/profile"),

  /**
   * Update the editable profile fields. `notifPrefs` is deliberately not among them: the server
   * strips it, because nothing reads it — per-category email consent is the real control.
   */
  updateProfile: (patch: { name?: string; jobTitle?: string | null; timezone?: string }) =>
    developerRequest<{ updated: boolean; message?: string }>("/api/auth/profile", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /** Change the password. Succeeds only with the current one, and signs every OTHER device out. */
  changePassword: (input: { currentPassword: string; newPassword: string }) =>
    developerRequest<{ changed: boolean; message: string }>("/api/auth/profile/change-password", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /** Active sessions (unexpired refresh tokens) — the device list. Degrades to [] server-side. */
  profileSessions: () => developerRequest<{ sessions: ApiAccountSession[] }>("/api/auth/profile/sessions"),

  /** Re-send the address-verification email to the signed-in account. */
  resendVerification: () =>
    developerRequest<{ message: string }>("/api/auth/resend-verification", { method: "POST" }),

  /**
   * Ask support to change the email or phone. Deliberately NOT a direct write — the server records
   * the request and a human handles it, because a self-serve email change is an account-takeover
   * primitive. Returns the message to show verbatim.
   */
  requestContactChange: (input: { field: "email" | "phone"; newValue: string; reason?: string }) =>
    developerRequest<{ submitted: boolean; message: string }>("/api/auth/profile/request-change", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  /** The workspace row. Readable by any MEMBER (not just the owner). */
  workspace: () => developerRequest<{ workspace: ApiWorkspace }>("/api/v1/workspaces/me"),

  /**
   * Rename the workspace. OWNER only (403 otherwise) and optimistically locked: pass the `version`
   * that was read, and a concurrent edit answers 409 rather than silently overwriting.
   */
  updateWorkspace: (patch: { name?: string; version?: number }) =>
    developerRequest<{ workspace: ApiWorkspace }>("/api/v1/workspaces/me", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  /**
   * Sandbox mode: while on, the dispatcher delivers ONLY to the listed recipients and blocks
   * everything else with reason "sandbox". Wrapped in the `{data}` envelope by the server.
   */
  sandbox: async (): Promise<ApiSandbox> =>
    (await developerRequest<{ data: ApiSandbox }>("/api/sandbox")).data,

  /** Enable/disable sandbox mode and set its allowed recipients. OWNER only. */
  updateSandbox: async (input: { sandboxMode: boolean; sandboxRecipients: string[] }): Promise<ApiSandbox> =>
    (await developerRequest<{ data: ApiSandbox }>("/api/sandbox", {
      method: "PUT",
      body: JSON.stringify(input),
    })).data,

  /** How long a closed account can be restored, so the deletion screen can state it truthfully. */
  accountRetention: () =>
    developerRequest<{ retentionDays: number; recordRetentionDays: number }>("/api/auth/account/retention"),

  /**
   * Close the account. OWNER only, irreversible after the retention window, and scoped
   * server-side to the session's own workspace — no field the client sends can widen it.
   */
  deleteAccount: () =>
    developerRequest<{ deleted: boolean; retentionDays: number; purgeAfter: string; workspaceId: number }>(
      "/api/auth/account",
      { method: "DELETE" },
    ),

  // ─── Contacts / subscribers ─────────────────────────────────────────────────

  /**
   * A page of contacts, newest first, KEYSET-paged on `afterId`.
   *
   * Keyset not offset for the same reason the campaign recipient list is: an import can be writing
   * this table while it is read, and an offset would skip and repeat rows. `total` reflects the same
   * filters, so the UI can say "12 of 4,301".
   */
  contacts: (params: {
    limit?: number; afterId?: number;
    search?: string; status?: ApiContactStatus; listId?: number; segmentId?: number; tag?: string;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.afterId) q.set("afterId", String(params.afterId));
    if (params.search) q.set("search", params.search);
    if (params.status) q.set("status", params.status);
    if (params.listId) q.set("listId", String(params.listId));
    if (params.segmentId) q.set("segmentId", String(params.segmentId));
    if (params.tag) q.set("tag", params.tag);
    const qs = q.toString();
    return request<{ contacts: ApiContact[]; nextAfterId: string | null; pageSize: number; total: number }>(
      `/v1/email/contacts${qs ? `?${qs}` : ""}`,
    );
  },

  /** One contact and the lists it is on. */
  contact: (id: number) =>
    request<{ contact: ApiContact; lists: ApiContactListMembership[] }>(`/v1/email/contacts/${id}`),

  createContact: (input: {
    email: string; name?: string; firstName?: string; lastName?: string;
    company?: string; attributes?: Record<string, string>; tags?: string[]; notes?: string;
  }) =>
    request<{ contact: ApiContact }>("/v1/email/contacts", {
      method: "POST", body: JSON.stringify(input),
    }),

  /** The address is intentionally NOT patchable — the server refuses it (see the service header). */
  patchContact: (id: number, patch: {
    name?: string | null; firstName?: string | null; lastName?: string | null;
    company?: string | null; notes?: string | null; tags?: string[]; attributes?: Record<string, string>;
  }) =>
    request<{ contact: ApiContact }>(`/v1/email/contacts/${id}`, {
      method: "PATCH", body: JSON.stringify(patch),
    }),

  deleteContact: (id: number) =>
    request<{ id: string; deleted: boolean }>(`/v1/email/contacts/${id}`, { method: "DELETE" }),

  /**
   * Import contacts in bulk. Upserts by address and never fails the whole batch over one bad row —
   * every row comes back with an outcome, and suppressed addresses are stored AND counted rather
   * than silently dropped, so the count reconciles with the file.
   */
  importContacts: (contacts: Array<{
    email: string; name?: string; firstName?: string; lastName?: string;
    company?: string; attributes?: Record<string, string>; tags?: string[];
  }>, opts: { listId?: number; source?: string } = {}) =>
    request<ApiContactImportResult>("/v1/email/contacts/import", {
      method: "POST",
      body: JSON.stringify({ contacts, listId: opts.listId, source: opts.source }),
    }),

  /**
   * Unsubscribe or re-subscribe. Writes a suppression row, so it is identical to a recipient's
   * one-click link. `changed:false` means the contact was already in that state (a no-op). A
   * re-subscribe cannot clear a platform-level block: then `status` comes back `suppressed`.
   */
  setContactSubscription: (id: number, subscribed: boolean) =>
    request<{ status: ApiContactStatus; changed: boolean }>(`/v1/email/contacts/${id}/subscription`, {
      method: "POST", body: JSON.stringify({ subscribed }),
    }),

  /** A contact's email history, keyset-paged on the `before` timestamp. */
  contactActivity: (id: number, params: { limit?: number; before?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set("limit", String(params.limit));
    if (params.before) q.set("before", params.before);
    const qs = q.toString();
    return request<ApiContactActivity>(`/v1/email/contacts/${id}/activity${qs ? `?${qs}` : ""}`);
  },

  // ─── Lists ────────────────────────────────────────────────────────────────

  lists: (params: { includeArchived?: boolean } = {}) =>
    request<{ lists: ApiEmailList[] }>(`/v1/email/lists${params.includeArchived ? "?includeArchived=true" : ""}`),

  list: (id: number) => request<{ list: ApiEmailList }>(`/v1/email/lists/${id}`),

  createList: (input: { name: string; description?: string }) =>
    request<{ list: ApiEmailList }>("/v1/email/lists", { method: "POST", body: JSON.stringify(input) }),

  patchList: (id: number, patch: { name?: string; description?: string | null }) =>
    request<{ list: ApiEmailList }>(`/v1/email/lists/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  /** Archive, not delete — a sent campaign keeps a readable audience name. */
  archiveList: (id: number) =>
    request<{ list: ApiEmailList }>(`/v1/email/lists/${id}`, { method: "DELETE" }),

  addListMembers: (id: number, contactIds: number[]) =>
    request<ApiListMembershipChange>(`/v1/email/lists/${id}/members`, {
      method: "POST", body: JSON.stringify({ contactIds }),
    }),

  /** POST, not DELETE — the ids are a body, and a DELETE body is dropped by some proxies. */
  removeListMembers: (id: number, contactIds: number[]) =>
    request<ApiListMembershipChange>(`/v1/email/lists/${id}/members/remove`, {
      method: "POST", body: JSON.stringify({ contactIds }),
    }),

  // ─── Segments ───────────────────────────────────────────────────────────────

  /** The field catalogue the builder renders from — server-owned so it cannot offer a rejected field. */
  segmentFields: () => request<{ fields: ApiSegmentField[] }>("/v1/email/segments/fields"),

  segments: (params: { includeArchived?: boolean } = {}) =>
    request<{ segments: ApiEmailSegment[] }>(`/v1/email/segments${params.includeArchived ? "?includeArchived=true" : ""}`),

  segment: (id: number) => request<{ segment: ApiEmailSegment }>(`/v1/email/segments/${id}`),

  /** Count an UNSAVED definition — the builder's live preview. */
  previewSegment: (definition: ApiSegmentDefinition) =>
    request<{ definition: ApiSegmentDefinition; matchCount: number; sendableCount: number }>(
      "/v1/email/segments/preview",
      { method: "POST", body: JSON.stringify({ definition }) },
    ),

  createSegment: (input: { name: string; description?: string; definition: ApiSegmentDefinition }) =>
    request<{ segment: ApiEmailSegment }>("/v1/email/segments", { method: "POST", body: JSON.stringify(input) }),

  patchSegment: (id: number, patch: { name?: string; description?: string | null; definition?: ApiSegmentDefinition }) =>
    request<{ segment: ApiEmailSegment }>(`/v1/email/segments/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  archiveSegment: (id: number) =>
    request<{ segment: ApiEmailSegment }>(`/v1/email/segments/${id}`, { method: "DELETE" }),

  // ─── Audiences ────────────────────────────────────────────────────────────

  /** "This will reach N people (M sendable)" for the audience picker, without attaching anything. */
  describeAudience: (audience: ApiAudienceSource) =>
    request<ApiAudienceDescription>("/v1/email/audiences/describe", {
      method: "POST", body: JSON.stringify({ audience }),
    }),

  /** Attach an audience's sendable contacts to a DRAFT campaign (through the recipient writer). */
  attachCampaignAudience: (campaignId: number, audience: ApiAudienceSource) =>
    request<ApiAudienceAttachResult>(`/v1/email/campaigns/${campaignId}/audience`, {
      method: "POST", body: JSON.stringify({ audience }),
    }),

  /** Enrol an audience's sendable contacts into an active automation (through the enroller). */
  enrollSequenceAudience: (sequenceId: number, audience: ApiAudienceSource) =>
    request<ApiAudienceEnrollResult>(`/v1/email/sequences/${sequenceId}/audience`, {
      method: "POST", body: JSON.stringify({ audience }),
    }),
};

/**
 * The exact request a screen made, for the drawer's API tab.
 *
 * Shown verbatim so a developer debugging their own integration can copy it. This is the console
 * proving it is an API consumer rather than claiming to be one.
 */
export function asCurl(method: string, path: string): string {
  return `curl -X ${method} ${window.location.origin}${path} \\\n  -H "Authorization: Bearer pa_live_…"`;
}
